import React, { memo, useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

/**
 * Collapsible card with a clickable header.
 *
 * @param {{
 *   title?: string,
 *   description?: string,           // small line of text under the title
 *   leading?: React.ReactNode,      // element shown before the title (icon chip, brand mark…)
 *   badge?: React.ReactNode,        // element shown beside the title (status pill, count…)
 *   star?: boolean,                 // shows a red asterisk after the title (required-section marker)
 *   className?: string,
 *   children?: React.ReactNode,
 *   dir?: string,
 *   isOpen?: boolean,               // initial open state (default: true)
 * }} props
 */
const Card = ({
  title,
  description,
  leading,
  badge,
  star,
  className,
  children,
  dir,
  isOpen: initialIsOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);

  const toggleCard = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    setIsOpen(initialIsOpen);
  }, [initialIsOpen]);

  // When `leading` is present, the brand mark sits where the title would
  // otherwise have its ps-3 clearance, so we skip the extra padding.
  const hasLeading = Boolean(leading);

  return (
    <div dir={dir} className={`card ${className || ""}`}>
      {title && (
        <button
          type="button"
          dir="rtl"
          onClick={toggleCard}
          className={`group relative flex w-full items-center gap-3 px-4 py-3.5 text-start transition-colors hover:bg-gray-50/70 dark:hover:bg-gray-700/30 md:gap-4 md:px-5 md:py-4 ${
            isOpen ? "border-b border-gray-100 dark:border-gray-700/60" : ""
          }`}
        >
          {/* Start-side accent stripe (hidden behind `leading` when present, by design) */}
          <span
            aria-hidden
            className={`absolute start-0 top-1/2 h-7 w-1 -translate-y-1/2 rounded-e-full bg-gradient-to-b from-purple-700 to-purple-950 transition-opacity duration-200 dark:from-purple-400 dark:to-purple-700 ${
              isOpen ? "opacity-100" : "opacity-0 group-hover:opacity-50"
            }`}
          />

          {/* Leading slot (brand mark / icon chip) */}
          {leading && <span className="relative shrink-0">{leading}</span>}

          {/* Title + optional badge + optional description */}
          <div className="relative min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h4
                className={`text-base font-bold tracking-tight text-gray-900 dark:text-gray-300 md:text-lg ${
                  hasLeading ? "" : "ps-3"
                }`}
              >
                {title}
                {star && <span className="star ms-1 text-rose-500">*</span>}
              </h4>
              {badge}
            </div>
            {description && (
              <p
                className={`mt-0.5 line-clamp-1 text-[12.5px] text-gray-500 dark:text-gray-400 ${
                  hasLeading ? "" : "ps-3"
                }`}
              >
                {description}
              </p>
            )}
          </div>

          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 transition-all duration-300 group-hover:bg-purple-50 group-hover:text-purple-700 dark:bg-gray-700/60 dark:text-gray-400 dark:group-hover:bg-purple-900/40 dark:group-hover:text-purple-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <ChevronDownIcon className="h-4 w-4" strokeWidth={2.5} />
          </span>
        </button>
      )}
      {isOpen && <div className="px-4 pb-4 pt-3 md:px-5">{children}</div>}
    </div>
  );
};

export default memo(Card);
